import React, {memo} from "react";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputBase,
    Link,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import SendIcon from '@mui/icons-material/Send';
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import {AGENTIC_SECTION_RESPONSE_STATUS} from "./agenticSectionState";
import AgenticSectionMarkdown from "./AgenticSectionMarkdown";

function buildAuthorHref(author) {
    const authorDisplayName = typeof author?.displayName === 'string' ? author.displayName.trim() : '';
    const authorHandle = authorDisplayName
        ? authorDisplayName.toLowerCase().split(/\s+/).filter(Boolean).join('.')
        : '';

    return authorHandle ? `/experts/${encodeURIComponent(authorHandle)}` : '';
}

function buildCitationReportHref(citation) {
    const reportId = typeof citation?.docId === 'string' ? citation.docId.trim() : '';

    return reportId ? `/products/${encodeURIComponent(reportId)}` : '';
}

function buildAuthorIdentity(author = {}) {
    return author.uid || author.email || author.displayName || '';
}

function collectUniqueCitationAuthors(citations = []) {
    const byIdentity = new Map();

    citations.forEach((citation) => {
        (citation?.authors || []).forEach((author) => {
            const identity = buildAuthorIdentity(author);
            if (!identity || byIdentity.has(identity)) return;
            byIdentity.set(identity, author);
        });
    });

    return Array.from(byIdentity.values());
}

function buildCitationReportKey(citation, index) {
    const reportId = typeof citation?.report?.id === 'string' ? citation.report.id.trim() : '';
    if (reportId) return `report:${reportId}`;

    const docId = typeof citation?.docId === 'string' ? citation.docId.trim() : '';
    if (docId) return `doc:${docId}`;

    const reportTitle = typeof citation?.report?.title === 'string' ? citation.report.title.trim() : '';
    if (reportTitle) return `title:${reportTitle.toLowerCase()}`;

    const title = typeof citation?.title === 'string' ? citation.title.trim() : '';
    if (title) return `title:${title.toLowerCase()}`;

    return `citation:${citation?.citationId || index}`;
}

function buildCitationReportLabel(citation, index) {
    return citation?.report?.title || citation?.title || citation?.citationId || `Citation ${index + 1}`;
}

function buildCitationPieceLabel(citation, reportLabel, index) {
    const citationTitle = typeof citation?.title === 'string' ? citation.title.trim() : '';
    const normalizedReportLabel = typeof reportLabel === 'string' ? reportLabel.trim().toLowerCase() : '';

    if (citationTitle && citationTitle.toLowerCase() !== normalizedReportLabel) {
        return citationTitle;
    }

    return citation?.citationId ? `Piece ${index + 1}` : '';
}

function groupCitationsByReport(citations = []) {
    const groups = new Map();

    citations.forEach((citation, index) => {
        const key = buildCitationReportKey(citation, index);
        const existingGroup = groups.get(key);

        if (existingGroup) {
            existingGroup.citations.push(citation);
            if (!existingGroup.href) {
                existingGroup.href = buildCitationReportHref(citation);
            }
            return;
        }

        groups.set(key, {
            key,
            label: buildCitationReportLabel(citation, index),
            href: buildCitationReportHref(citation),
            citations: [citation],
        });
    });

    return Array.from(groups.values()).map((group) => ({
        ...group,
        authors: collectUniqueCitationAuthors(group.citations),
    }));
}

function CitationAuthors({authors = []}) {
    const normalizedAuthors = Array.isArray(authors) ? authors.filter(Boolean) : [];
    if (!normalizedAuthors.length) return null;

    return normalizedAuthors.map((author, index) => {
        const label = author?.displayName || author?.email || author?.uid || 'Unknown author';
        const href = buildAuthorHref(author);

        return (
            <React.Fragment key={`${author?.uid || author?.email || label}-${index}`}>
                {index > 0 ? ', ' : null}
                {href ? (
                    <Link href={href} underline="hover" color="inherit" target="_blank" rel="noopener noreferrer">
                        {label}
                    </Link>
                ) : label}
            </React.Fragment>
        );
    });
}

function summarizeFeedbackEntries(feedbackEntries = []) {
    const latestEntries = [...(feedbackEntries || [])]
        .sort((left, right) => (right.createdAtMillis || 0) - (left.createdAtMillis || 0));

    const latestByUser = new Map();
    latestEntries.forEach((entry, index) => {
        const key = entry?.userId || entry?.id || `feedback-${index}`;
        if (!latestByUser.has(key)) {
            latestByUser.set(key, entry);
        }
    });

    return Array.from(latestByUser.values()).reduce((summary, entry) => {
        if (entry?.direction === 'down') {
            summary.dislikes.push(entry);
        } else {
            summary.likes.push(entry);
        }
        return summary;
    }, {likes: [], dislikes: []});
}

function formatFeedbackSummary(entries = []) {
    return entries
        .map((entry) => {
            const author = entry?.userLabel || entry?.userId || 'Unknown user';
            const comment = typeof entry?.comment === 'string' ? entry.comment.trim() : '';
            return comment ? `${author} — ${comment}` : author;
        })
        .join(' · ');
}

const AgenticSectionPromptRow = memo(function AgenticSectionPromptRow({
                                                                          promptText,
                                                                          canSubmit,
                                                                          isBusy,
                                                                          inputRef,
                                                                          onPromptChange,
                                                                          onTextKeyDown,
                                                                          onSubmitClick,
                                                                      }) {
    return (
        <Paper component="div" elevation={2} className="pm-agentic-section__prompt-row">
            <InputBase
                className="pm-agentic-section__field"
                fullWidth
                placeholder="Describe agentic section..."
                value={promptText || ""}
                onChange={onPromptChange}
                inputRef={inputRef}
                onKeyDown={onTextKeyDown}
                inputProps={{
                    "aria-label": "Describe agentic section",
                }}
            />

            <IconButton
                color="primary"
                onClick={onSubmitClick}
                className="pm-agentic-section__submit"
                disabled={!canSubmit || isBusy}
                aria-label={isBusy ? "Generating suggestion" : "Submit prompt"}
            >
                {isBusy ? <CircularProgress size={18}/> : <SendIcon fontSize="small"/>}
            </IconButton>
        </Paper>
    );
});

const AgenticSectionReadyResponse = memo(function AgenticSectionReadyResponse({
                                                                                  visibleResponseData,
                                                                                  feedbackSummary,
                                                                                  likeSummaryText,
                                                                                  dislikeSummaryText,
                                                                                  hasFeedbackSummary,
                                                                                  onCitationGroupClick,
                                                                                  onOpenFeedback,
                                                                              }) {
    return (
        <Stack spacing={2} className="pm-agentic-section__response-wrap">
            <AgenticSectionMarkdown
                markdown={visibleResponseData.markdown}
                citations={visibleResponseData.citations}
                onCitationGroupClick={onCitationGroupClick}
            />

            <Stack direction="row" spacing={1} alignItems="center">
                <IconButton size="small" onClick={() => onOpenFeedback?.("up")} aria-label="Thumbs up feedback">
                    <ThumbUpOutlinedIcon fontSize="small"/>
                </IconButton>
                <IconButton size="small" onClick={() => onOpenFeedback?.("down")} aria-label="Thumbs down feedback">
                    <ThumbDownOutlinedIcon fontSize="small"/>
                </IconButton>
            </Stack>

            {hasFeedbackSummary ? (
                <Stack spacing={0.75} className="pm-agentic-section__feedback-indicator">
                    {feedbackSummary.likes.length ? (
                        <Stack direction="row" spacing={0.75} alignItems="flex-start"
                               className="pm-agentic-section__feedback-row pm-agentic-section__feedback-row--up">
                            <ThumbUpOutlinedIcon fontSize="inherit"
                                                 className="pm-agentic-section__feedback-icon"/>
                            <Typography variant="caption" className="pm-agentic-section__feedback-text">
                                Liked by {likeSummaryText}
                            </Typography>
                        </Stack>
                    ) : null}

                    {feedbackSummary.dislikes.length ? (
                        <Stack direction="row" spacing={0.75} alignItems="flex-start"
                               className="pm-agentic-section__feedback-row pm-agentic-section__feedback-row--down">
                            <ThumbDownOutlinedIcon fontSize="inherit"
                                                   className="pm-agentic-section__feedback-icon"/>
                            <Typography variant="caption" className="pm-agentic-section__feedback-text">
                                Disliked by {dislikeSummaryText}
                            </Typography>
                        </Stack>
                    ) : null}
                </Stack>
            ) : null}
        </Stack>
    );
});

function AgenticSectionFeedbackDialog({direction, open, comment, onClose, onCommentChange, onSubmit}) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {direction === "up" ? "What worked well?" : "What should improve?"}
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    multiline
                    minRows={3}
                    value={comment}
                    onChange={(event) => onCommentChange?.(event.target.value)}
                    placeholder="Optional feedback"
                    sx={{mt: 1}}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={onSubmit} variant="contained">Send</Button>
            </DialogActions>
        </Dialog>
    );
}

function AgenticSectionCitationsDialog({citationGroup, onClose}) {
    const citations = citationGroup?.citations || [];
    const reportGroups = groupCitationsByReport(citations);

    return (
        <Dialog
            open={Boolean(citations.length)}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            scroll="paper"
            disableScrollLock
            PaperProps={{sx: {maxHeight: 'min(80vh, 720px)'}}}
        >
            <DialogTitle>
                {citations.length} citation{citations.length === 1 ? '' : 's'}
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5}>
                    {reportGroups.map((reportGroup, reportIndex) => (
                        <Stack key={reportGroup.key} spacing={1}>
                            <Stack spacing={0.5}>
                                <Typography variant="body2" fontWeight={600} component="div">
                                    {reportGroup.href ? (
                                        <Link
                                            href={reportGroup.href}
                                            underline="hover"
                                            color="inherit"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {reportGroup.label}
                                        </Link>
                                    ) : (
                                        reportGroup.label
                                    )}
                                </Typography>
                                {reportGroup.authors?.length ? (
                                    <Typography variant="caption" color="text.secondary">
                                        <CitationAuthors authors={reportGroup.authors}/>
                                    </Typography>
                                ) : null}
                            </Stack>

                            <Stack spacing={1}>
                                {reportGroup.citations.map((citation, citationIndex) => {
                                    const pieceLabel = buildCitationPieceLabel(citation, reportGroup.label, citationIndex);
                                    return (
                                        <Stack key={citation.citationId || `${reportGroup.key}-${citationIndex}`}
                                               spacing={0.5}>
                                            {pieceLabel ? (
                                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                    {pieceLabel}
                                                </Typography>
                                            ) : null}
                                            {citation.excerpt ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    {citation.excerpt}
                                                </Typography>
                                            ) : null}
                                            {citationIndex < reportGroup.citations.length - 1 ?
                                                <Divider sx={{pt: 0.5}}/> : null}
                                        </Stack>
                                    );
                                })}
                            </Stack>
                            {reportIndex < reportGroups.length - 1 ? <Divider sx={{pt: 0.5}}/> : null}
                        </Stack>
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

export const AgenticSectionControls = memo(function AgenticSectionControls({
                                                                               promptText,
                                                                               selectedAgent,
                                                                               responseStatus,
                                                                               responseText,
                                                                               responseData,
                                                                               errorMessage,
                                                                               feedbackEntries = [],
                                                                               canSubmit,
                                                                               isSubmitting,
                                                                               onPromptTextChange,
                                                                               onSubmit,
                                                                               inputRef,
                                                                               onInputKeyDown,
                                                                               feedbackDialogOpen = false,
                                                                               feedbackDirection = "up",
                                                                               feedbackComment = "",
                                                                               onOpenFeedback,
                                                                               onCloseFeedbackDialog,
                                                                               onFeedbackCommentChange,
                                                                               onSubmitFeedback,
                                                                               activeCitationGroup = null,
                                                                               onCitationGroupClick,
                                                                               onCloseCitationGroup,
                                                                           }) {
    const handleSubmit = () => {
        if (!canSubmit || isSubmitting || !selectedAgent) return;
        onSubmit(promptText || "", selectedAgent);
    };

    const handlePromptChange = (event) => {
        onPromptTextChange(event.target.value);
    };

    const handleTextKeyDown = (event) => {
        onInputKeyDown(event);
        if (event.defaultPrevented) return;

        const isSubmitEnter = event.key === "Enter"
            && !event.shiftKey
            && !event.altKey
            && !event.ctrlKey
            && !event.metaKey
            && !event.isComposing;

        if (!isSubmitEnter) return;

        event.preventDefault();
        event.stopPropagation();
        handleSubmit();
    };

    const isQueued = responseStatus === AGENTIC_SECTION_RESPONSE_STATUS.QUEUED;
    const isGenerating = responseStatus === AGENTIC_SECTION_RESPONSE_STATUS.GENERATING;
    const visibleResponseData = responseData?.markdown?.trim()
        ? responseData
        : {
            markdown: typeof responseText === 'string' ? responseText.trim() : '',
            text: typeof responseText === 'string' ? responseText.trim() : '',
            citations: [],
            authors: [],
        };
    const isReady = responseStatus === AGENTIC_SECTION_RESPONSE_STATUS.READY
        && Boolean(visibleResponseData?.markdown || responseText);
    const isError = responseStatus === AGENTIC_SECTION_RESPONSE_STATUS.ERROR;
    const isBusy = isQueued || isSubmitting || isGenerating;
    const feedbackSummary = summarizeFeedbackEntries(feedbackEntries);
    const likeSummaryText = formatFeedbackSummary(feedbackSummary.likes);
    const dislikeSummaryText = formatFeedbackSummary(feedbackSummary.dislikes);
    const hasFeedbackSummary = Boolean(feedbackSummary.likes.length || feedbackSummary.dislikes.length);

    return (
        <Stack spacing={1} className="pm-agentic-section__controls">
            <AgenticSectionPromptRow
                promptText={promptText}
                canSubmit={canSubmit}
                isBusy={isBusy}
                inputRef={inputRef}
                onPromptChange={handlePromptChange}
                onTextKeyDown={handleTextKeyDown}
                onSubmitClick={handleSubmit}
            />


            {isReady ? (
                <AgenticSectionReadyResponse
                    visibleResponseData={visibleResponseData}
                    feedbackSummary={feedbackSummary}
                    likeSummaryText={likeSummaryText}
                    dislikeSummaryText={dislikeSummaryText}
                    hasFeedbackSummary={hasFeedbackSummary}
                    onCitationGroupClick={onCitationGroupClick}
                    onOpenFeedback={onOpenFeedback}
                />
            ) : null}

            {isError ? (
                <Typography variant="body2" color="error">
                    {errorMessage || "Failed to generate suggestion. Try Submit again."}
                </Typography>
            ) : null}

            <AgenticSectionFeedbackDialog
                direction={feedbackDirection}
                open={feedbackDialogOpen}
                comment={feedbackComment}
                onClose={onCloseFeedbackDialog}
                onCommentChange={onFeedbackCommentChange}
                onSubmit={onSubmitFeedback}
            />

            <AgenticSectionCitationsDialog
                citationGroup={activeCitationGroup}
                onClose={onCloseCitationGroup}
            />
        </Stack>
    );
});

